/**
 * The live socket for the note on screen (PLAN.md D3, "Live socket (phase 2)").
 *
 * One `LiveNoteClient` per open note while online, in the foreground and with
 * `collabFeatures.live` on (`hooks/useLiveNote.ts` decides). It speaks the
 * protocol of `apps/worker/src/do/noteDoc.ts`: `hello {clientId, rev}`, then
 * `welcome`, `change`, `stale`, `snapshot`, `presence`; every message has a
 * `type`. Every message runs through the same engine (`@caliu/utils`
 * noteContentSync) and the same storage choke point as the HTTP driver
 * (`services/contentSync.ts`), so a change seen over both is applied once:
 * the engine ignores revs it has seen.
 *
 *  - Submits come from `prepareSubmit`, one change in flight at a time, all
 *    under the client id the socket said hello with (the server attributes a
 *    socket's submits to it).
 *  - A dropped socket reconnects with a jittered backoff and says hello from
 *    its rev, so it resumes where it was. The backoff resets only once a
 *    socket proved healthy (its own change came back, or 30 s welcomed), and
 *    a gap's fresh hello goes through it after the first, so a server that
 *    welcomes and then fails is not hammered.
 *  - 4503 (live off, or the owner paused sharing: access is kept), 4429 (rate limited) and 1008 (a protocol error, our
 *    bug) hand the note to HTTP for a minute; 4401/4403/4404/4413 stop (the
 *    HTTP path has its own answer for each; 4403 also tells `onAccessLost`).
 *  - `access {role}` (phase 3) says the person's role on the note changed.
 *
 * The socket, timers and storage are injected, so this runs in a test over a
 * fake WebSocket.
 */

import {
  prepareSubmit,
  receiveChanges,
  receiveSnapshot,
  isIdentityOp,
  type NoteSyncState,
  type RemoteChange,
  type TextOp,
} from "@caliu/utils";
import { Sentry } from "../sentry";
import type { ContentSyncStore, SyncDeviceSource } from "./contentSync";

export const LIVE_CLOSE = {
  unauthenticated: 4401,
  noAccess: 4403,
  gone: 4404,
  tooLarge: 4413,
  rateLimited: 4429,
  disabled: 4503,
  badMessage: 1008,
} as const;

/**
 * How long a hello waits for its welcome. The note is the socket's from hello
 * on (HTTP leaves it alone), so a server that never answers would hold it:
 * close and hand it to HTTP for a minute.
 */
export const LIVE_WELCOME_TIMEOUT_MS = 10_000;
/** How long HTTP carries the note after a 4503, 4429 or 1008 (or no welcome). */
export const LIVE_FALLBACK_MS = 60_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
/** Closes in a row without a welcome before HTTP takes over for a minute. */
const MAX_UNWELCOMED_ATTEMPTS = 3;
/** A welcomed socket open this long counts as healthy (the backoff resets). */
export const LIVE_HEALTHY_MS = 30_000;

export interface LivePerson {
  userId: string;
  name: string;
  since: string;
}

/** The part of a browser `WebSocket` the client uses. */
export interface LiveSocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: (() => void) | null;
}

export type LiveStore = Pick<
  ContentSyncStore,
  "loadState" | "saveState" | "noteTitle" | "clearCatchUp" | "createOrphanCopy" | "recordInflight"
>;

export interface LiveClock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  random(): number;
}

export interface LiveNoteDeps {
  noteId: string;
  open: (noteId: string) => LiveSocketLike;
  store: LiveStore;
  device: SyncDeviceSource;
  /** Caller rule 2: flush the editor's typing before remote ops land. */
  beforeRemote?: (noteId: string) => void;
  onPresence?: (people: LivePerson[]) => void;
  /** The socket is (or stopped being) the note's transport. */
  onLive?: (live: boolean) => void;
  /** HTTP takes the note over for a while: run a pass now. */
  onFallback?: () => void;
  /** The server changed this person's role on the note (`access {role}`). */
  onAccess?: (role: "owner" | "editor" | "viewer") => void;
  /** The socket closed with 4403: this person lost access to the note. */
  onAccessLost?: () => void;
  clock?: LiveClock;
}

const realClock: LiveClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  random: () => Math.random(),
};

// ── Which notes are live (read by the HTTP driver and the editor) ─────────

const clients = new Map<string, LiveNoteClient>();
let fallbackUntil = 0;

/** The note's socket said hello: HTTP leaves its text to the socket. */
export function isLiveNote(noteId: string): boolean {
  return clients.get(noteId)?.isLive === true;
}

/** A local edit was stored: the note's socket submits it. */
export function kickLiveNote(noteId: string): void {
  clients.get(noteId)?.submit();
}

/** Whether HTTP carries every note right now (after a 4503, 4429 or 1008). */
export function liveFallbackActive(now = Date.now()): boolean {
  return now < fallbackUntil;
}

/** Tests only. */
export function resetLiveFallback(): void {
  fallbackUntil = 0;
}

type Incoming =
  | { type: "welcome"; rev: number; changes?: RemoteChange[]; snapshot?: SnapshotBody; presence?: { people: LivePerson[] } }
  | { type: "change"; rev: number; clientId: string; seq: number; ops: TextOp; at: string }
  | { type: "stale"; seq: number; headRev: number }
  | ({ type: "snapshot" } & SnapshotBody)
  | { type: "presence"; people: LivePerson[] }
  | { type: "access"; role: string };

interface SnapshotBody {
  rev: number;
  text: string;
  at: string;
  yourLastSeq: number;
  reset?: boolean;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export class LiveNoteClient {
  private ws: LiveSocketLike | null = null;
  private stopped = true;
  private welcomed = false;
  /**
   * Hello is sent on the current socket. The note is the socket's from then
   * on: an HTTP pass between hello and welcome would put a change in flight
   * the welcome's `yourLastSeq` knows nothing about.
   */
  private helloSent = false;
  /** The client id this socket said hello with: every submit on it is that client's. */
  private socketClientId: string | null = null;
  /** The in-flight seq already sent on this socket (no resend until a new socket or a stale). */
  private sentSeq: number | null = null;
  private submitting = false;
  private submitAgain = false;
  /** Reconnects since the last healthy socket (the backoff's exponent). */
  private attempts = 0;
  /** Closes in a row without a welcome. */
  private unwelcomed = 0;
  private timer: unknown = null;
  private healthTimer: unknown = null;
  private welcomeTimer: unknown = null;
  private readonly clock: LiveClock;

  constructor(private deps: LiveNoteDeps) {
    this.clock = deps.clock ?? realClock;
  }

  /** The socket carries the note's text (from hello on, until it drops). */
  get isLive(): boolean {
    return (this.helloSent || this.welcomed) && !this.stopped;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    clients.set(this.deps.noteId, this);
    this.connect();
  }

  /** Note switch, background, offline, sign-out: close and forget. */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    if (clients.get(this.deps.noteId) === this) clients.delete(this.deps.noteId);
    this.clearTimer();
    this.drop(1000, "closed");
  }

  /** Sends what the note has unsent, if anything (a no-op until welcome). */
  submit(): void {
    void this.trySubmit();
  }

  // ── Connection ──────────────────────────────────────────────────────────

  private connect(): void {
    if (this.stopped) return;
    const now = this.clock.now();
    if (now < fallbackUntil) {
      this.schedule(fallbackUntil - now);
      return;
    }
    const s = this.deps.store.loadState(this.deps.noteId);
    if (!s) {
      // Left the engine (legacy mode): nothing to say hello with.
      this.stop();
      return;
    }
    let ws: LiveSocketLike;
    try {
      ws = this.deps.open(this.deps.noteId);
    } catch (err) {
      Sentry.captureException(err, { tags: { operation: "live_socket_open" } });
      this.reconnectLater();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      const st = this.deps.store.loadState(this.deps.noteId);
      if (!st) {
        this.stop();
        return;
      }
      // The in-flight change's client, so the welcome's `yourLastSeq` (and a
      // resend) are about it.
      this.socketClientId = st.inflight?.clientId ?? this.deps.device.clientId;
      this.helloSent = true;
      ws.send(JSON.stringify({ type: "hello", clientId: this.socketClientId, rev: st.base === null ? 0 : st.rev }));
      this.welcomeTimer = this.clock.setTimeout(() => {
        this.welcomeTimer = null;
        if (this.ws !== ws || this.welcomed) return;
        this.drop(1000, "no welcome");
        this.unwelcomed = 0;
        this.attempts = 0;
        this.fallBack();
      }, LIVE_WELCOME_TIMEOUT_MS);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      this.onMessage(ev.data);
    };
    ws.onerror = () => {
      // The close that follows says what happened.
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.onClose(ev.code);
    };
  }

  /** Detaches and closes the current socket (no close handling). */
  private drop(code: number, reason: string): void {
    const ws = this.ws;
    this.ws = null;
    const wasLive = this.welcomed;
    this.welcomed = false;
    this.helloSent = false;
    this.sentSeq = null;
    this.socketClientId = null;
    if (this.healthTimer !== null) this.clock.clearTimeout(this.healthTimer);
    this.healthTimer = null;
    if (this.welcomeTimer !== null) this.clock.clearTimeout(this.welcomeTimer);
    this.welcomeTimer = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try {
        ws.close(code, reason);
      } catch {
        // Already closing. Expected.
      }
    }
    if (wasLive) this.deps.onLive?.(false);
  }

  /**
   * A gap, or a new client id: say hello again from the stored rev, now the
   * first time, through the backoff after that until a socket is healthy.
   */
  private restart(): void {
    this.drop(1000, "resync");
    if (this.attempts === 0) {
      this.attempts++;
      this.connect();
    } else this.reconnectLater();
  }

  /** The socket works: the next drop starts the backoff from the bottom. */
  private healthy(): void {
    this.attempts = 0;
    if (this.healthTimer !== null) this.clock.clearTimeout(this.healthTimer);
    this.healthTimer = null;
  }

  private onClose(code: number): void {
    const wasWelcomed = this.welcomed;
    this.drop(1000, "closed");
    if (this.stopped) return;
    switch (code) {
      case LIVE_CLOSE.noAccess:
        // Removed (or the link went off): the pull takes the note away and
        // keeps unsent text as a copy.
        this.stop();
        this.deps.onAccessLost?.();
        this.deps.onFallback?.();
        return;
      case LIVE_CLOSE.unauthenticated:
      case LIVE_CLOSE.gone:
      case LIVE_CLOSE.tooLarge:
        // HTTP has its own answer (sign-out, not_found, too_large).
        this.stop();
        this.deps.onFallback?.();
        return;
      case LIVE_CLOSE.badMessage:
        // A message the server could not read: a client bug.
        Sentry.captureException(new Error("live socket closed with 1008"), { extra: { noteId: this.deps.noteId } });
        this.fallBack();
        return;
      case LIVE_CLOSE.disabled:
        // Live off, or the owner's sharing is paused (the kill switch): not
        // lost access. HTTP carries the note (and answers `paused`).
      case LIVE_CLOSE.rateLimited:
        this.fallBack();
        return;
      default:
        // A dropped connection or a deploy: expected, reconnect. An upgrade
        // refused before any welcome (a 503 when live is off reads as 1006
        // in a browser) hands over to HTTP after a few tries.
        if (!wasWelcomed) this.unwelcomed++;
        if (!wasWelcomed && this.unwelcomed >= MAX_UNWELCOMED_ATTEMPTS + 1) {
          this.unwelcomed = 0;
          this.attempts = 0;
          this.fallBack();
          return;
        }
        this.reconnectLater();
    }
  }

  private fallBack(): void {
    fallbackUntil = this.clock.now() + LIVE_FALLBACK_MS;
    this.deps.onFallback?.();
    this.schedule(LIVE_FALLBACK_MS);
  }

  private reconnectLater(): void {
    const cap = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.attempts);
    this.attempts++;
    // Full jitter on the upper half, so a deploy's reconnects spread out.
    this.schedule(cap / 2 + this.clock.random() * (cap / 2));
  }

  private schedule(ms: number): void {
    this.clearTimer();
    this.timer = this.clock.setTimeout(() => {
      this.timer = null;
      this.connect();
    }, ms);
  }

  private clearTimer(): void {
    if (this.timer !== null) this.clock.clearTimeout(this.timer);
    this.timer = null;
  }

  // ── Messages ────────────────────────────────────────────────────────────

  private onMessage(data: unknown): void {
    let msg: unknown;
    try {
      msg = JSON.parse(typeof data === "string" ? data : "");
    } catch (err) {
      Sentry.captureException(err, { tags: { operation: "live_socket_message" } });
      return;
    }
    if (!isRecord(msg) || typeof msg.type !== "string") return;
    const m = msg as Incoming;
    let gap = false;
    try {
      switch (m.type) {
        case "welcome":
          this.welcomed = true;
          this.unwelcomed = 0;
          if (this.welcomeTimer !== null) this.clock.clearTimeout(this.welcomeTimer);
          this.welcomeTimer = null;
          this.healthTimer = this.clock.setTimeout(() => {
            this.healthTimer = null;
            this.healthy();
          }, LIVE_HEALTHY_MS);
          this.deps.onLive?.(true);
          if (m.snapshot) this.receiveSnapshot(m.snapshot);
          else if (m.changes?.length) gap = this.receiveChanges(m.changes);
          if (m.presence) this.deps.onPresence?.(m.presence.people ?? []);
          this.deps.store.clearCatchUp(this.deps.noteId);
          break;
        case "change":
          // Our own change back (its ack): the socket works end to end.
          if (m.clientId === this.socketClientId && m.seq === this.sentSeq) this.healthy();
          gap = this.receiveChanges([{ rev: m.rev, clientId: m.clientId, seq: m.seq, ops: m.ops, at: m.at }]);
          break;
        case "stale":
          // The missing changes came first as `change`; the in-flight change
          // was folded into the buffer and goes again under a new seq. A stale
          // about an older seq (one already resent) changes nothing.
          if (m.seq === this.sentSeq) this.sentSeq = null;
          break;
        case "snapshot":
          this.receiveSnapshot(m);
          break;
        case "presence":
          this.deps.onPresence?.(m.people ?? []);
          return;
        case "access":
          if (m.role === "owner" || m.role === "editor" || m.role === "viewer") {
            // A viewer's change in flight never lands; the caller keeps it
            // as a copy and drops it (`keepUnsentAsCopy`).
            this.deps.onAccess?.(m.role);
          }
          return;
        default:
          // Anything newer: not ours yet.
          return;
      }
    } catch (err) {
      // A broken state or storage error: reported; a later hello resyncs.
      Sentry.captureException(err, { extra: { noteId: this.deps.noteId, type: m.type } });
      this.drop(1000, "error");
      this.reconnectLater();
      return;
    }
    if (gap) {
      // A rev was skipped: a fresh hello resumes from the stored rev.
      this.restart();
      return;
    }
    void this.trySubmit();
  }

  /** True when the changes skipped a rev. */
  private receiveChanges(changes: RemoteChange[]): boolean {
    return this.receive((s) => receiveChanges(s, changes));
  }

  private receiveSnapshot(snap: SnapshotBody): void {
    void this.receive((s) => {
      const r = receiveSnapshot(s, {
        rev: snap.rev,
        text: snap.text,
        at: snap.at,
        yourLastSeq: snap.yourLastSeq,
        reset: snap.reset,
      });
      return { state: r.state, needsCatchUp: false, orphan: r.orphan };
    });
  }

  /** One engine step on the stored state, in one synchronous turn. True on a gap. */
  private receive(step: (s: NoteSyncState) => { state: NoteSyncState; needsCatchUp: boolean; orphan?: string }): boolean {
    const { store, noteId } = this.deps;
    this.deps.beforeRemote?.(noteId);
    const s = store.loadState(noteId);
    if (!s) return false;
    const r = step(s);
    if (r.orphan !== undefined) store.createOrphanCopy(noteId, r.orphan);
    if (r.state !== s) store.saveState(noteId, s, r.state);
    if (r.state.inflight === null || r.state.inflight.seq !== this.sentSeq) this.sentSeq = null;
    return r.needsCatchUp;
  }

  // ── Submits ─────────────────────────────────────────────────────────────

  private async trySubmit(): Promise<void> {
    if (!this.welcomed || !this.ws) return;
    if (this.submitting) {
      this.submitAgain = true;
      return;
    }
    this.submitting = true;
    try {
      do {
        this.submitAgain = false;
        await this.submitOnce();
      } while (this.submitAgain && this.welcomed);
    } catch (err) {
      Sentry.captureException(err, { extra: { noteId: this.deps.noteId, op: "live_submit" } });
    } finally {
      this.submitting = false;
    }
  }

  private async submitOnce(): Promise<void> {
    const { store, noteId, device } = this.deps;
    const first = store.loadState(noteId);
    if (!first || first.base === null || first.rev === 0) return;
    if (first.inflight) {
      if (first.inflight.seq === this.sentSeq) return; // waiting for its ack
      if (first.inflight.clientId !== this.socketClientId) {
        this.restart(); // hello again as the in-flight change's client
        return;
      }
      // Durable before it leaves, a resend too (an earlier record may have failed).
      const ws = this.ws;
      await store.recordInflight?.(noteId, first.inflight);
      if (this.ws !== ws || !this.welcomed) return;
      const now = store.loadState(noteId);
      if (now?.inflight?.seq !== first.inflight.seq || now.inflight.seq === this.sentSeq) return;
      this.send(now);
      return;
    }
    if (isIdentityOp(first.buffer)) return;
    if (this.socketClientId !== device.clientId) {
      // This tab's id changed since hello (a new seq counter): a new socket.
      this.restart();
      return;
    }
    // The seq first (it may wait on another tab); the state is read again
    // after, in the same turn as the write that puts it in flight.
    const seq = await device.allocateSeq();
    const ws = this.ws;
    if (!this.welcomed || !ws) return;
    const s = store.loadState(noteId);
    if (!s || s.inflight || s.base === null || isIdentityOp(s.buffer)) {
      if (s?.inflight) this.submitAgain = true;
      return;
    }
    const r = prepareSubmit(s, { clientId: this.socketClientId, nextSeq: seq });
    // Stored, and on disk, BEFORE it is sent: a seq is never reused. A record
    // that fails throws: nothing is sent, the next submit tries again.
    if (r.state !== s) store.saveState(noteId, s, r.state);
    if (r.request?.kind !== "submit" || !r.state.inflight) return;
    await store.recordInflight?.(noteId, r.state.inflight);
    // A socket that closed meanwhile resends it after the next welcome.
    if (this.ws !== ws || !this.welcomed) return;
    this.send(r.state);
  }

  /** Sends the state's change in flight (prepareSubmit hands it back as is). */
  private send(s: NoteSyncState): void {
    const r = prepareSubmit(s, { clientId: this.socketClientId ?? "", nextSeq: 0 });
    const req = r.request;
    if (req?.kind !== "submit" || !this.ws) return;
    const title = this.deps.store.noteTitle(this.deps.noteId);
    this.ws.send(
      JSON.stringify({
        type: "submit",
        baseRev: req.baseRev,
        baseHash: req.baseHash,
        seq: req.seq,
        ops: req.ops,
        ...(title !== undefined ? { title } : {}),
      }),
    );
    this.sentSeq = req.seq;
  }
}
